import React, { useState, useEffect, useRef } from 'react';
import './scroll-fix.css'; // Simple CSS fix for scroll issues

const SimpleMobileScroll = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [position, setPosition] = useState({ 
    x: 10, 
    y: typeof window !== 'undefined' ? (window.innerHeight / 2) - 80 : 20 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, startX: null, startY: null });
  const containerRef = useRef(null);
  const dragHandleRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    const updateInitialPosition = () => {
      // Center vertically on window resize if position hasn't been manually moved much
      if (position.x <= 50 && position.y <= window.innerHeight / 2 + 100) {
        setPosition(prev => ({
          x: 10,
          y: (window.innerHeight / 2) - 80
        }));
      }
    };
    
    checkMobile();
    updateInitialPosition();
    
    window.addEventListener('resize', checkMobile);
    window.addEventListener('resize', updateInitialPosition);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('resize', updateInitialPosition);
    };
  }, []);

  // Drag functionality - only for drag handle
  const handleDragStart = (e) => {
    const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    const clientY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;
    
    setDragStart({
      x: clientX - position.x,
      y: clientY - position.y,
      startX: clientX,
      startY: clientY
    });
    
    e.preventDefault();
  };

  // Move handlers outside to avoid dependency issues
  const handleMouseMove = (e) => {
    const dragStartRef = dragStart;
    if (!dragStartRef.startX) return;
    
    const clientX = e.clientX;
    const clientY = e.clientY;
    
    // Calculate distance moved
    const deltaX = Math.abs(clientX - dragStartRef.startX);
    const deltaY = Math.abs(clientY - dragStartRef.startY);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Only start dragging if we've moved more than 5 pixels
    if (distance > 5 && !isDragging) {
      setIsDragging(true);
    }
    
    if (isDragging || distance > 5) {
      const newX = Math.max(0, Math.min(window.innerWidth - 60, clientX - dragStartRef.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 120, clientY - dragStartRef.y));
      
      setPosition({ x: newX, y: newY });
    }
    
    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    const dragStartRef = dragStart;
    if (!dragStartRef.startX) return;
    
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;
    
    // Calculate distance moved
    const deltaX = Math.abs(clientX - dragStartRef.startX);
    const deltaY = Math.abs(clientY - dragStartRef.startY);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Only start dragging if we've moved more than 5 pixels
    if (distance > 5 && !isDragging) {
      setIsDragging(true);
    }
    
    if (isDragging || distance > 5) {
      const newX = Math.max(0, Math.min(window.innerWidth - 60, clientX - dragStartRef.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 120, clientY - dragStartRef.y));
      
      setPosition({ x: newX, y: newY });
    }
    
    e.preventDefault();
  };

  const handleMouseUp = (e) => {
    setIsDragging(false);
    setDragStart({ x: 0, y: 0, startX: null, startY: null });
    
    // Force blur on the drag handle specifically
    if (dragHandleRef.current) {
      dragHandleRef.current.blur();
    }
    
    // Also remove focus from any active element
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    setDragStart({ x: 0, y: 0, startX: null, startY: null });
    
    // Force blur on the drag handle specifically
    if (dragHandleRef.current) {
      dragHandleRef.current.blur();
    }
    
    // Also remove focus from any active element
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  };

  // Mouse events
  useEffect(() => {
    if (dragStart.startX !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragStart.startX]);

  // Touch events
  useEffect(() => {
    if (dragStart.startX !== null) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [dragStart.startX]);

  // Cleanup effect to ensure drag handle is deselected when component unmounts or drag state changes
  useEffect(() => {
    if (!isDragging && dragHandleRef.current) {
      // Small delay to ensure state has settled
      const timer = setTimeout(() => {
        if (dragHandleRef.current) {
          dragHandleRef.current.blur();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isDragging]);

  const scrollUp = () => {
    // Simple scroll up functionality
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    const newPosition = Math.max(0, currentScroll - 300);
    
    window.scrollTo({
      top: newPosition,
      behavior: 'smooth'
    });
  };

  const scrollDown = () => {
    // Simple scroll down functionality
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    const newPosition = currentScroll + 300;
    
    window.scrollTo({
      top: newPosition,
      behavior: 'smooth'
    });
  };

  if (!isMobile) return null;

  return (
    <div 
      ref={containerRef}
      className={`mobile-scroll-arrows ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div 
        ref={dragHandleRef}
        className="drag-handle"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        tabIndex="-1"
      >
        ⋮⋮
      </div>
      <button
        onClick={scrollUp}
        className="scroll-arrow scroll-up"
        type="button"
      >
        ↑
      </button>
      <button
        onClick={scrollDown}
        className="scroll-arrow scroll-down"
        type="button"
      >
        ↓
      </button>
    </div>
  );
};

export default SimpleMobileScroll;
